import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AiPlanActiveSwitch from "../AiPlanActiveSwitch";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST,
  AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
  deleteConditionalPlan,
  updateConditionalPlanActive,
} from "../../../services/autoTaggingService";
import DeleteTaggingPlanConfirmDialog from "../TaggingPlan/DeleteTaggingPlanConfirmDialog";
import TaggingPlanActiveConfirmDialog, {
  ENHANCED_TAGGING_RULE_ACTIVE_COPY,
  type TaggingPlanActiveConfirmMode,
} from "../TaggingPlan/TaggingPlanActiveConfirmDialog";
import TaggingPlanRowActions from "../TaggingPlan/TaggingPlanRowActions";
import EnhancedTaggingRuleTableToolbar from "./EnhancedTaggingRuleTableToolbar";
import type { EnhancedTaggingRuleRow } from "./types";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";

const ENHANCED_TAGGING_RULE_DELETE_DESCRIPTION =
  "Are you sure you want to delete this enhanced tagging rule? This action cannot be undone. Tags that have already been applied will not be removed.";

type EnhancedTaggingRuleTableProps = Readonly<{
  rows: EnhancedTaggingRuleRow[];
  onAddNew?: () => void;
  onEdit?: (row: EnhancedTaggingRuleRow) => void;
  onRuleUpdated?: () => void;
}>;

function EnhancedTaggingRuleNoRowsOverlay({
  onAddNew,
}: {
  onAddNew?: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.{" "}
        {onAddNew ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddNew}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Add Now
          </Link>
        ) : null}
      </Typography>
    </Box>
  );
}

export default function EnhancedTaggingRuleTable({
  rows,
  onAddNew,
  onEdit,
  onRuleUpdated,
}: EnhancedTaggingRuleTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { showMessage } = useSnackbar();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);
  const [deleteTarget, setDeleteTarget] = useState<EnhancedTaggingRuleRow | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    row: EnhancedTaggingRuleRow;
    nextActive: boolean;
  } | null>(null);
  const [updatingActive, setUpdatingActive] = useState(false);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const noRowsOverlaySlot = useCallback(
    () => (
      <EnhancedTaggingRuleNoRowsOverlay
        onAddNew={canManageAutoTagging ? onAddNew : undefined}
      />
    ),
    [canManageAutoTagging, onAddNew],
  );

  const activeConfirmMode: TaggingPlanActiveConfirmMode | null =
    activeToggleTarget
      ? activeToggleTarget.nextActive
        ? "activate"
        : "deactivate"
      : null;

  const handleActiveToggleRequest = useCallback(
    (row: EnhancedTaggingRuleRow, nextActive: boolean) => {
      setActiveToggleTarget({ row, nextActive });
    },
    [],
  );

  const handleDeleteRequest = useCallback((row: EnhancedTaggingRuleRow) => {
    setDeleteTarget(row);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleting) return;
    setDeleteTarget(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await deleteConditionalPlan(deleteTarget.id);
      showMessage("Enhanced tagging rule deleted successfully.", "success");
      setDeleteTarget(null);
      onRuleUpdated?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to delete enhanced tagging rule.",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, onRuleUpdated, showMessage]);

  const handleCloseActiveDialog = useCallback(() => {
    if (updatingActive) return;
    setActiveToggleTarget(null);
  }, [updatingActive]);

  const handleConfirmActiveToggle = useCallback(async () => {
    if (!activeToggleTarget || updatingActive) return;

    const { row, nextActive } = activeToggleTarget;
    setUpdatingActive(true);
    try {
      await updateConditionalPlanActive(row.id, nextActive);
      showMessage(
        nextActive
          ? AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST
          : AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
        "success",
      );
      setActiveToggleTarget(null);
      onRuleUpdated?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update enhanced tagging rule status.",
        "error",
      );
    } finally {
      setUpdatingActive(false);
    }
  }, [activeToggleTarget, onRuleUpdated, showMessage, updatingActive]);

  const columns = useMemo<GridColDef<EnhancedTaggingRuleRow>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: "objectTypeName",
        headerName: "Object Type",
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.objectTypeName?.trim() || "-",
      },
      {
        field: "objectSubTypeName",
        headerName: "Object Sub-Type",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.objectSubTypeName?.trim() || "-",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          const isEmpty = !text || text === "-";
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: isEmpty ? "#8c8c8c" : "inherit",
                  fontSize: "14px",
                }}
              >
                {isEmpty ? "-" : text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "conditionDescription",
        headerName: "Condition Description",
        flex: 1.2,
        minWidth: 320,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          const isEmpty = !text;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Typography
                noWrap
                title={isEmpty ? undefined : text}
                sx={{
                  color: isEmpty ? "#8c8c8c" : "inherit",
                  fontSize: "14px",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                }}
              >
                {isEmpty ? "-" : text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "dimension",
        headerName: "Dimension",
        width: 180,
        sortable: false,
        filterable: false,
      },
      {
        field: "value",
        headerName: "Value",
        width: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: "enabled",
        headerName: "Active",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-plan-status-cell",
        renderCell: ({ row }) => (
          <AiPlanActiveSwitch
            checked={row.enabled}
            disabled={!canManageAutoTagging}
            onChange={(nextChecked) =>
              handleActiveToggleRequest(row, nextChecked)
            }
          />
        ),
      },
      ...(canManageAutoTagging
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 88,
              sortable: false,
              filterable: false,
              resizable: false,
              disableExport: true,
              align: "center" as const,
              headerAlign: "center" as const,
              cellClassName: "ai-plan-actions-cell",
              renderCell: ({ row }: { row: EnhancedTaggingRuleRow }) => (
                <TaggingPlanRowActions
                  onEdit={() => onEdit?.(row)}
                  onDelete={() => handleDeleteRequest(row)}
                />
              ),
            } satisfies GridColDef<EnhancedTaggingRuleRow>,
          ]
        : []),
    ],
    [canManageAutoTagging, handleActiveToggleRequest, handleDeleteRequest, onEdit],
  );

  const isEmpty = rows.length === 0;

  return (
    <>
      <Box
        className={`${styles.dataGridPanel}${
          isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
        }`}
        sx={{
          ...aiPlanDataGridPanelSx,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 480,
        }}
      >
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={48}
          columnHeaderHeight={48}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          disableColumnSorting
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20, 50]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          showToolbar
          showColumnVerticalBorder={false}
          disableColumnPinning={false}
          pinnedColumnsSectionSeparator="border"
          slots={{
            toolbar: EnhancedTaggingRuleTableToolbar,
            noRowsOverlay: noRowsOverlaySlot,
            columnsManagement: columnsManagementSlot,
          }}
          slotProps={{
            toolbar: {
              onAddNew,
            } as never,
          }}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } },
            pinnedColumns: {
              right: canManageAutoTagging
                ? ["enabled", "actions"]
                : ["enabled"],
            },
          }}
          sx={
            [
              getRegulationsListDataGridSx(primaryColor),
              ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
              {
                height: "100%",
                minHeight: 400,
                "& .ai-plan-status-cell, & .ai-plan-actions-cell": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  "& .MuiDataGrid-cellContent": {
                    display: "flex !important",
                    alignItems: "center !important",
                    justifyContent: "center !important",
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                  },
                },
                "& .ai-plan-actions-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
                "& .ai-plan-status-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
              },
            ] as SxProps<Theme>
          }
        />
      </Box>
      <DeleteTaggingPlanConfirmDialog
        open={deleteTarget !== null}
        deleting={deleting}
        description={ENHANCED_TAGGING_RULE_DELETE_DESCRIPTION}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => void handleConfirmDelete()}
      />
      <TaggingPlanActiveConfirmDialog
        open={activeToggleTarget !== null}
        mode={activeConfirmMode}
        saving={updatingActive}
        copyByMode={ENHANCED_TAGGING_RULE_ACTIVE_COPY}
        onClose={handleCloseActiveDialog}
        onConfirm={() => void handleConfirmActiveToggle()}
      />
    </>
  );
}
